import { useEffect, useRef, useState } from "react";

/**
 * Anima um número de 0 até ao valor final quando o utilizador chega ao
 * banner. Re-dispara sempre que a secção volta a entrar no viewport
 * (scroll para baixo E para cima).
 *
 * Aceita strings como "+150", "+45.000", "24h", "89%", "+200.000".
 * Preserva prefixo/sufixo e usa separador pt-PT.
 *
 * Comportamento:
 * - A animação SÓ arranca quando o elemento entra na "zona central"
 *   do viewport (rootMargin apertado). Isto garante que o utilizador
 *   vê o efeito de contagem no momento em que "chega" ao banner e
 *   não muito antes.
 * - Ao sair do viewport, o contador é reposto a 0 para poder animar
 *   novamente quando voltar a entrar.
 */
export default function AnimatedNumber({ value, duration = 2000, className = "" }) {
  const ref = useRef(null);

  const match = String(value).match(/^([^\d]*)([\d.,]+)(.*)$/);
  const prefix = match?.[1] || "";
  const numStr = match?.[2] || "0";
  const suffix = match?.[3] || "";
  const target = parseInt(numStr.replace(/[.,]/g, ""), 10) || 0;

  const [display, setDisplay] = useState(0);
  // Chave que incrementa cada vez que o elemento (re)entra em viewport
  // — faz com que o useEffect de animação corra novamente do início.
  const [runKey, setRunKey] = useState(0);
  const [active, setActive] = useState(false);
  // Ref para evitar re-disparar a animação enquanto o elemento continua visível.
  const activeRef = useRef(false);

  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            // Arranca a animação quando o contador aparece no ecrã.
            // Só (re)inicia se ainda não estava ativo — não reinicia durante o scroll.
            if (!activeRef.current) {
              activeRef.current = true;
              setDisplay(0);
              setActive(true);
              setRunKey((k) => k + 1);
            }
          } else {
            // Só repõe a 0 quando o contador está TOTALMENTE fora do ecrã
            // (threshold 0 + rootMargin 0 => isIntersecting=false apenas quando
            // nenhuma parte está visível). Assim nunca fica a 0 enquanto visível.
            activeRef.current = false;
            setActive(false);
            setDisplay(0);
          }
        });
      },
      // Viewport completo: entra assim que qualquer parte aparece; sai só quando
      // o elemento deixa totalmente o ecrã.
      { threshold: 0, rootMargin: "0px" }
    );

    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!active || target <= 0) return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const effectiveDuration = reduce ? Math.max(600, Math.round(duration * 0.4)) : duration;

    const start = performance.now();
    let raf = 0;
    const step = (now) => {
      const t = Math.min((now - start) / effectiveDuration, 1);
      // easing suave (easeOutQuart) — arranque rápido, desaceleração no final
      const eased = 1 - Math.pow(1 - t, 4);
      setDisplay(Math.floor(eased * target));
      if (t < 1) raf = requestAnimationFrame(step);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [runKey, active, target, duration]);

  return (
    <span ref={ref} className={className} data-testid="animated-number">
      {prefix}
      {display.toLocaleString("pt-PT").replace(/\s/g, ".")}
      {suffix}
    </span>
  );
}
